/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { captureReferral } from './lib/referral';
import RootLayout from './pages/_layout';

captureReferral();
import AuthLayout from './pages/auth/_layout';
import TabsLayout from './pages/tabs/_layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LandingPage from './pages/landing';

// Auth Pages
import Login from './pages/auth/login';
import Register from './pages/auth/register';
import ForgotPassword from './pages/auth/forgot-password';
import ResetPassword from './pages/auth/reset-password';

// Marketing / Legal Pages
import ServicesPage from './pages/marketing/services';
import FeaturesPage from './pages/marketing/features';
import AboutPage from './pages/marketing/about';
import CareersPage from './pages/marketing/careers';
import PressPage from './pages/marketing/press';
import FAQPage from './pages/marketing/faq';
import ContactPage from './pages/marketing/contact';
import PrivacyPolicyPage from './pages/marketing/privacy';
import TermsOfServicePage from './pages/marketing/terms';
import CookiePolicyPage from './pages/marketing/cookies';

// Tab Pages
import Dashboard from './pages/tabs/index';
import Messaging from './pages/tabs/messaging/index';
import Social from './pages/tabs/social/index';
import Commerce from './pages/tabs/commerce/index';
import Career from './pages/tabs/career/index';
import Payment from './pages/tabs/payment/index';
import Wallet from './pages/tabs/wallet/index';
import Notifications from './pages/tabs/notifications/index';
import Profile from './pages/tabs/profile/index';
import Affiliate from './pages/tabs/affiliate/index';
import CompanyLayout from './pages/tabs/company/_layout';
import CompanyDashboard from './pages/tabs/company/index';
import Industries from './pages/tabs/company/industries/index';
import Assessment from './pages/tabs/company/assessment/index';
import BusinessProfile from './pages/tabs/company/business/index';
import Plans from './pages/tabs/company/plans/index';
import Analytics from './pages/tabs/company/analytics/index';
import CompanyChat from './pages/tabs/company/chat/index';
import ErrorBoundary from './components/common/ErrorBoundary';

// Settings
import SettingsLayout from './pages/settings/_layout';
import SettingsHome from './pages/settings/index';
import Preferences from './pages/settings/preferences';
import Language from './pages/settings/language';
import SecuritySettings from './pages/settings/security';
import ChangePassword from './pages/settings/change-password';
import TwoFactor from './pages/settings/two-factor';
import AppLock from './pages/settings/app-lock';
import NotificationsSettings from './pages/settings/notifications';
import NotificationSounds from './pages/settings/notification-sounds';
import LocationSettings from './pages/settings/location';
import DataUsage from './pages/settings/data-usage';
import Support from './pages/settings/support';
import Help from './pages/settings/help';
import SupportContact from './pages/settings/contact';
import Report from './pages/settings/report';
import Legal from './pages/settings/legal';
import AboutSettings from './pages/settings/about';
import Account from './pages/settings/account';
import DeleteAccount from './pages/settings/delete-account';

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <LandingPage /> },

      // Marketing / Legal
      { path: 'services', element: <ServicesPage /> },
      { path: 'features', element: <FeaturesPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'careers', element: <CareersPage /> },
      { path: 'press', element: <PressPage /> },
      { path: 'faq', element: <FAQPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'privacy', element: <PrivacyPolicyPage /> },
      { path: 'terms', element: <TermsOfServicePage /> },
      { path: 'cookies', element: <CookiePolicyPage /> },

      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          { path: 'login', element: <Login /> },
          { path: 'register', element: <Register /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'reset-password', element: <ResetPassword /> },
          { index: true, element: <Navigate to="login" replace /> },
        ],
      },

      {
        path: 'app',
        element: <ProtectedRoute />,
        children: [
          {
            element: <TabsLayout />,
            children: [
              { index: true, element: <Dashboard /> },
              { path: 'messaging', element: <Messaging /> },
              { path: 'social', element: <Social /> },
              { path: 'commerce', element: <Commerce /> },
              { path: 'career', element: <Career /> },
              { path: 'payment', element: <Payment /> },
              { path: 'wallet', element: <Wallet /> },
              { path: 'notifications', element: <Notifications /> },
              { path: 'profile', element: <Profile /> },
              { path: 'affiliate', element: <Affiliate /> },
              {
                path: 'company',
                element: <CompanyLayout />,
                children: [
                  { index: true, element: <CompanyDashboard /> },
                  { path: 'industries', element: <Industries /> },
                  { path: 'assessment', element: <Assessment /> },
                  { path: 'business', element: <BusinessProfile /> },
                  { path: 'plans', element: <Plans /> },
                  { path: 'analytics', element: <Analytics /> },
                  { path: 'chat', element: <CompanyChat /> },
                ],
              },
              {
                path: 'settings',
                element: <SettingsLayout />,
                children: [
                  { index: true, element: <SettingsHome /> },
                  { path: 'preferences', element: <Preferences /> },
                  { path: 'preferences/language', element: <Language /> },
                  { path: 'security', element: <SecuritySettings /> },
                  { path: 'security/change-password', element: <ChangePassword /> },
                  { path: 'security/two-factor', element: <TwoFactor /> },
                  { path: 'security/app-lock', element: <AppLock /> },
                  { path: 'notifications', element: <NotificationsSettings /> },
                  { path: 'notifications/sounds', element: <NotificationSounds /> },
                  { path: 'location', element: <LocationSettings /> },
                  { path: 'location/data-usage', element: <DataUsage /> },
                  { path: 'support', element: <Support /> },
                  { path: 'support/help', element: <Help /> },
                  { path: 'support/contact', element: <SupportContact /> },
                  { path: 'support/report', element: <Report /> },
                  { path: 'legal', element: <Legal /> },
                  { path: 'about', element: <AboutSettings /> },
                  { path: 'account', element: <Account /> },
                  { path: 'account/delete', element: <DeleteAccount /> },
                ],
              },
            ],
          },
        ],
      },
      { index: true, element: <Navigate to="/auth/login" replace /> },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
